import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Camera, Upload, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import logoImg from '../assets/logo.png';
import GooglePlacesInput from '../components/GooglePlacesInput';
import ImageCropModal from '../components/ImageCropModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NativeSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 py-2 rounded-xl bg-[hsl(144,18%,97%)] border border-[hsl(144,18%,85%)] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)] focus:border-[hsl(144,18%,45%)]"
      data-testid={testId}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function SimpleToggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-[hsl(144,18%,45%)]' : 'bg-gray-200'}`}
      data-testid={testId}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const TEST_BLOCKS = [
  {
    id: 'gastos', title: 'Gastos comunes', emoji: '🌡️',
    options: [
      { value: 1, label: 'En invierno me gusta tener la calefacción alta todo el día.' },
      { value: 2, label: 'En invierno suelo poner la calefacción por la noche y usar ropa de abrigo.' },
      { value: 3, label: 'Vivo con la calefacción ajustada.' },
    ],
  },
  {
    id: 'social', title: 'Vida social y visitas', emoji: '🎉',
    options: [
      { value: 1, label: 'No me gusta estar mucho tiempo en soledad; suelo recibir personas en casa.' },
      { value: 2, label: 'Podría convivir con personas amigas o conocidas un par de fines de semana al mes.' },
      { value: 3, label: 'Es poco habitual que reciba muchas visitas en casa.' },
    ],
  },
  {
    id: 'espacios', title: 'Uso de espacios comunes', emoji: '🏠',
    options: [
      { value: 1, label: 'Suelo hacer vida en los espacios comunes y me gusta compartirlos.' },
      { value: 2, label: 'Me adapto al uso de los espacios comunes.' },
      { value: 3, label: 'Me gusta mantener mis cosas organizadas.' },
    ],
  },
  {
    id: 'animo', title: 'Ánimo y estilo de vida', emoji: '✨',
    options: [
      { value: 1, label: 'Por lo general tengo energía y soy una persona activa en casa.' },
      { value: 2, label: 'Mi estado de ánimo puede variar.' },
      { value: 3, label: 'Prefiero un ambiente tranquilo.' },
    ],
  },
  {
    id: 'tareas', title: 'Tareas del hogar', emoji: '🧹',
    options: [
      { value: 1, label: 'Suelo repartir y cumplir con las tareas del hogar.' },
      { value: 2, label: 'Cada persona se encarga de lo que ensucia en el momento.' },
      { value: 3, label: 'Me interesa contratar ayuda para las tareas del hogar.' },
    ],
  },
];

function calcRoommateProfile(answers) {
  const total = Object.values(answers).reduce((s, v) => s + v, 0);
  if (total <= 8) return 'Abierto';
  if (total <= 12) return 'Equilibrado';
  return 'Reservado';
}

const ALL_ACTIVITIES = [
  'Deportes', 'Cine', 'Videojuegos', 'Lectura', 'Fiesta',
  'Instrumentos musicales', 'Vino y charla', '4 20', 'Cantar', 'Cocinar',
  'Redes sociales', 'Senderismo', 'Gimnasio', 'Running', 'Viajar',
  'Yoga', 'Bailar', 'Moda', 'Animales', 'Jardinería',
  'Tecnología', 'Motor', 'Pintura', 'Tapear', 'Festivales y conciertos', 'Compras',
];

const NATIONALITY_OPTIONS = [
  { value: 'Española', label: '🇪🇸 España' }, { value: 'Alemana', label: '🇩🇪 Alemania' },
  { value: 'Francesa', label: '🇫🇷 Francia' }, { value: 'Italiana', label: '🇮🇹 Italia' },
  { value: 'Portuguesa', label: '🇵🇹 Portugal' }, { value: 'Británica', label: '🇬🇧 Reino Unido' },
  { value: 'Holandesa', label: '🇳🇱 Holanda' }, { value: 'Belga', label: '🇧🇪 Bélgica' },
  { value: 'Sueca', label: '🇸🇪 Suecia' }, { value: 'Noruega', label: '🇳🇴 Noruega' },
  { value: 'Danesa', label: '🇩🇰 Dinamarca' }, { value: 'Finlandesa', label: '🇫🇮 Finlandia' },
  { value: 'Polaca', label: '🇵🇱 Polonia' }, { value: 'Rumana', label: '🇷🇴 Rumanía' },
  { value: 'Griega', label: '🇬🇷 Grecia' }, { value: 'Checa', label: '🇨🇿 República Checa' },
  { value: 'Húngara', label: '🇭🇺 Hungría' }, { value: 'Austriaca', label: '🇦🇹 Austria' },
  { value: 'Suiza', label: '🇨🇭 Suiza' }, { value: 'Americana', label: '🇺🇸 Estados Unidos' },
  { value: 'Mexicana', label: '🇲🇽 México' }, { value: 'Argentina', label: '🇦🇷 Argentina' },
  { value: 'Colombiana', label: '🇨🇴 Colombia' }, { value: 'Venezolana', label: '🇻🇪 Venezuela' },
  { value: 'Peruana', label: '🇵🇪 Perú' }, { value: 'Chilena', label: '🇨🇱 Chile' },
  { value: 'Brasileña', label: '🇧🇷 Brasil' }, { value: 'Ecuatoriana', label: '🇪🇨 Ecuador' },
  { value: 'Boliviana', label: '🇧🇴 Bolivia' }, { value: 'Uruguaya', label: '🇺🇾 Uruguay' },
  { value: 'Paraguaya', label: '🇵🇾 Paraguay' }, { value: 'China', label: '🇨🇳 China' },
  { value: 'Japonesa', label: '🇯🇵 Japón' }, { value: 'Coreana', label: '🇰🇷 Corea del Sur' },
  { value: 'India', label: '🇮🇳 India' }, { value: 'Pakistaní', label: '🇵🇰 Pakistán' },
  { value: 'Marroquí', label: '🇲🇦 Marruecos' }, { value: 'Argelina', label: '🇩🇿 Argelia' },
  { value: 'Senegalesa', label: '🇸🇳 Senegal' }, { value: 'Nigeriana', label: '🇳🇬 Nigeria' },
  { value: 'Otra', label: '🌍 Otra' },
];

const PROFILE_STYLES = {
  Abierto:     'bg-pink-100 text-pink-700 border-pink-200',
  Equilibrado: 'bg-orange-100 text-orange-700 border-orange-200',
  Reservado:   'bg-violet-100 text-violet-700 border-violet-200',
};

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [cropModalSrc, setCropModalSrc] = useState(null);

  const [testAnswers, setTestAnswers] = useState({
    gastos: null, social: null, espacios: null, animo: null, tareas: null,
  });
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [formData, setFormData] = useState({
    name: user?.name || '', age: '', city: '', neighborhood: '', budget: '',
    move_in_date: '', stay_duration: '', occupation: '', bio: '', preferences: '',
    cleaning_habits: '', sociability: '', noise_habits: '',
    is_smoker: false, accepts_smokers: true, has_pets: false, accepts_pets: true,
    schedule: '', contact_email: user?.email || '', contact_link: '',
    allow_direct_contact: false, gender: '', nationality: '',
    lgbtq_friendly: false, erasmus: false,
    preferred_flatmate_gender: '', num_flatmates: '', offers_room: '',
  });

  const totalSteps = 6;
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('La imagen es demasiado grande (máx 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => setCropModalSrc(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (blob) => {
    const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(blob));
    setCropModalSrc(null);
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    const formDataFile = new FormData();
    formDataFile.append('file', photoFile);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/profile/photo`, formDataFile, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data', ...(token && { Authorization: `Bearer ${token}` }) },
      });
      return response.data.path;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al subir la foto');
      return null;
    }
  };

  const handleNext = () => {
    if (step === 5) {
      const allAnswered = TEST_BLOCKS.every(b => testAnswers[b.id] !== null);
      if (!allAnswered) { toast.error('Responde todas las preguntas para continuar'); return; }
    }
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const toggleActivity = (activity) => {
    setSelectedActivities(prev => {
      if (prev.includes(activity)) return prev.filter(a => a !== activity);
      if (prev.length >= 6) { toast.error('Selecciona máximo 6 actividades'); return prev; }
      return [...prev, activity];
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let photoPath = null;
      if (photoFile) photoPath = await uploadPhoto();
      const allAnswered = TEST_BLOCKS.every(b => testAnswers[b.id] !== null);
      const roommateProfile = allAnswered ? calcRoommateProfile(testAnswers) : null;
      const profileData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        budget: formData.budget ? parseInt(formData.budget) : null,
        profile_photo: photoPath || user?.profile_photo || null,
        roommate_profile: roommateProfile,
        activity_tags: selectedActivities,
        gender: formData.gender || null,
        nationality: formData.nationality || null,
        preferred_flatmate_gender: formData.preferred_flatmate_gender || null,
        num_flatmates: formData.num_flatmates || null,
        offers_room: formData.offers_room || null,
      };
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/profile`, profileData, {
        withCredentials: true,
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      await refreshUser();
      toast.success('¡Perfil completado!');
      navigate('/feed', { replace: true });
    } catch (error) {
      toast.error('Error al guardar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Hombre' }, { value: 'female', label: 'Mujer' },
    { value: 'prefer_not_to_say', label: 'Prefiero no decirlo' },
  ];
  const preferredFlatmateGenderOptions = [
    { value: 'male', label: 'Hombres' }, { value: 'female', label: 'Mujeres' },
    { value: 'any', label: 'Indiferente' },
  ];
  const numFlatmatesOptions = [
    { value: '1', label: '1 persona' }, { value: '2', label: '2 personas' },
    { value: '3_or_more', label: '3 o más personas' }, { value: 'any', label: 'Indiferente' },
  ];
  const stayDurationOptions = [
    { value: '1-3 meses', label: '1-3 meses' }, { value: '3-6 meses', label: '3-6 meses' },
    { value: '6-12 meses', label: '6-12 meses' }, { value: 'Más de 1 año', label: 'Más de 1 año' },
    { value: 'Indefinido', label: 'Indefinido' },
  ];
  const cleaningOptions = [
    { value: 'Muy ordenado', label: 'Muy ordenado' }, { value: 'Ordenado', label: 'Ordenado' },
    { value: 'Normal', label: 'Normal' }, { value: 'Relajado', label: 'Relajado' },
  ];
  const sociabilityOptions = [
    { value: 'Muy sociable', label: 'Muy sociable' }, { value: 'Sociable', label: 'Sociable' },
    { value: 'Equilibrado', label: 'Equilibrado' }, { value: 'Reservado', label: 'Reservado' },
    { value: 'Muy reservado', label: 'Muy reservado' },
  ];
  const noiseOptions = [
    { value: 'Muy silencioso', label: 'Muy silencioso' }, { value: 'Silencioso', label: 'Silencioso' },
    { value: 'Normal', label: 'Normal' }, { value: 'A veces ruidoso', label: 'A veces ruidoso' },
  ];
  const scheduleOptions = [
    { value: 'Madrugador', label: 'Madrugador' }, { value: 'Horario normal', label: 'Horario normal' },
    { value: 'Noctámbulo', label: 'Noctámbulo' }, { value: 'Horario variable', label: 'Horario variable' },
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Información básica</h2>
              <p className="text-gray-600 mt-2">Cuéntanos sobre ti</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full bg-[hsl(144,18%,93%)] border-2 border-dashed border-[hsl(144,18%,70%)] flex items-center justify-center cursor-pointer hover:border-[hsl(144,18%,55%)] transition-colors overflow-hidden" onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-area">
                {photoPreview || user?.profile_photo ? (
                  <img src={photoPreview || (user?.profile_photo?.startsWith('http') ? user.profile_photo : `${API}/files/${user.profile_photo}`)} alt="Preview" className="w-full h-full object-cover" />
                ) : <Camera className="w-8 h-8 text-[hsl(144,18%,55%)]" />}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="photo-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)] flex items-center gap-1">
                <Upload className="w-4 h-4" />Subir foto
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="name">Nombre *</Label><Input id="name" value={formData.name} onChange={(e) => updateField('name', e.target.value)} className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" required data-testid="name-input" /></div>
              <div><Label htmlFor="age">Edad *</Label><Input id="age" type="number" min="18" max="99" value={formData.age} onChange={(e) => updateField('age', e.target.value)} className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" required data-testid="age-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Género</Label><div className="mt-1"><NativeSelect value={formData.gender} onChange={(v) => updateField('gender', v)} placeholder="Selecciona" options={genderOptions} testId="gender-select" /></div></div>
              <div><Label>Nacionalidad</Label><div className="mt-1"><NativeSelect value={formData.nationality} onChange={(v) => updateField('nationality', v)} placeholder="Selecciona" options={NATIONALITY_OPTIONS} testId="nationality-select" /></div></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="city">Ciudad *</Label><GooglePlacesInput value={formData.city} onChange={(value) => updateField('city', value)} placeholder="Ej: Madrid, Barcelona..." className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] w-full h-10 px-3 py-2 border border-[hsl(144,18%,85%)] focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)]" /></div>
              <div><Label>Zona / Barrio</Label><GooglePlacesInput type="neighborhood" value={formData.neighborhood} onChange={(value) => updateField('neighborhood', value)} placeholder="Ej: Malasaña, Gràcia..." className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] w-full h-10 px-3 py-2 border border-[hsl(144,18%,85%)] focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)]" /></div>
            </div>
            <div><Label htmlFor="occupation">Ocupación</Label><Input id="occupation" value={formData.occupation} onChange={(e) => updateField('occupation', e.target.value)} placeholder="Estudiante, trabajador..." className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" data-testid="occupation-input" /></div>
            <div><Label htmlFor="bio">Biografía corta</Label><Textarea id="bio" value={formData.bio} onChange={(e) => updateField('bio', e.target.value)} placeholder="Cuéntanos un poco sobre ti..." className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)] min-h-[100px]" data-testid="bio-input" /></div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-[hsl(144,18%,93%)] rounded-xl">
                <div><p className="font-medium text-gray-900">🏳️‍🌈 LGBTQ+ Friendly</p><p className="text-sm text-gray-500">Ambiente inclusivo y respetuoso</p></div>
                <SimpleToggle checked={formData.lgbtq_friendly} onChange={(v) => updateField('lgbtq_friendly', v)} testId="lgbtq-switch" />
              </div>
              <div className="flex items-center justify-between p-4 bg-[hsl(144,18%,93%)] rounded-xl">
                <div><p className="font-medium text-gray-900">✈️ Erasmus</p><p className="text-sm text-gray-500">Estoy en programa Erasmus o similar</p></div>
                <SimpleToggle checked={formData.erasmus} onChange={(v) => updateField('erasmus', v)} testId="erasmus-switch" />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Detalles de búsqueda</h2>
              <p className="text-gray-600 mt-2">¿Qué estás buscando?</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="budget">Presupuesto mensual (€)</Label><Input id="budget" type="number" min="0" value={formData.budget} onChange={(e) => updateField('budget', e.target.value)} placeholder="Ej: 500" className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" data-testid="budget-input" /></div>
              <div><Label htmlFor="move_in_date">Fecha de entrada</Label><Input id="move_in_date" type="date" value={formData.move_in_date} onChange={(e) => updateField('move_in_date', e.target.value)} className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" data-testid="move-in-date-input" /></div>
            </div>
            <div><Label>Duración prevista</Label><div className="mt-1"><NativeSelect value={formData.stay_duration} onChange={(v) => updateField('stay_duration', v)} placeholder="Selecciona duración" options={stayDurationOptions} testId="stay-duration-select" /></div></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Quiero convivir con</Label><div className="mt-1"><NativeSelect value={formData.preferred_flatmate_gender} onChange={(v) => updateField('preferred_flatmate_gender', v)} placeholder="Selecciona" options={preferredFlatmateGenderOptions} testId="preferred-flatmate-gender-select" /></div></div>
              <div><Label>Número de compañeros</Label><div className="mt-1"><NativeSelect value={formData.num_flatmates} onChange={(v) => updateField('num_flatmates', v)} placeholder="Selecciona" options={numFlatmatesOptions} testId="num-flatmates-select" /></div></div>
            </div>
            <div><Label htmlFor="preferences">Preferencias de convivencia</Label><Textarea id="preferences" value={formData.preferences} onChange={(e) => updateField('preferences', e.target.value)} placeholder="¿Qué buscas en un compañero de piso ideal?" className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)] min-h-[100px]" data-testid="preferences-input" /></div>
            <div>
              <Label>🏠 Ofrezco habitación</Label>
              <div className="mt-1">
                <NativeSelect
                  value={formData.offers_room}
                  onChange={(v) => updateField('offers_room', v)}
                  placeholder="No ofrezco habitación"
                  options={[
                    { value: '1', label: '1 habitación' },
                    { value: '2', label: '2 habitaciones' },
                    { value: 'more_than_2', label: 'Más de 2 habitaciones' },
                  ]}
                  testId="offers-room-select"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Estilo de vida</h2>
              <p className="text-gray-600 mt-2">Tus hábitos de convivencia</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Hábitos de limpieza</Label><div className="mt-1"><NativeSelect value={formData.cleaning_habits} onChange={(v) => updateField('cleaning_habits', v)} placeholder="Selecciona" options={cleaningOptions} testId="cleaning-select" /></div></div>
              <div><Label>Nivel de sociabilidad</Label><div className="mt-1"><NativeSelect value={formData.sociability} onChange={(v) => updateField('sociability', v)} placeholder="Selecciona" options={sociabilityOptions} testId="sociability-select" /></div></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Hábitos de ruido</Label><div className="mt-1"><NativeSelect value={formData.noise_habits} onChange={(v) => updateField('noise_habits', v)} placeholder="Selecciona" options={noiseOptions} testId="noise-select" /></div></div>
              <div><Label>Horario habitual</Label><div className="mt-1"><NativeSelect value={formData.schedule} onChange={(v) => updateField('schedule', v)} placeholder="Selecciona" options={scheduleOptions} testId="schedule-select" /></div></div>
            </div>
            <div className="space-y-4 pt-4">
              {[
                { label: '¿Fumas?', sub: 'Indica si eres fumador', field: 'is_smoker', testId: 'is-smoker-switch' },
                { label: '¿Aceptas fumadores?', sub: '¿Te importa convivir con fumadores?', field: 'accepts_smokers', testId: 'accepts-smokers-switch' },
                { label: '¿Tienes mascotas?', sub: 'Indica si tienes animales', field: 'has_pets', testId: 'has-pets-switch' },
                { label: '¿Aceptas mascotas?', sub: '¿Te importa convivir con mascotas?', field: 'accepts_pets', testId: 'accepts-pets-switch' },
              ].map(({ label, sub, field, testId }) => (
                <div key={field} className="flex items-center justify-between p-4 bg-[hsl(144,18%,93%)] rounded-xl">
                  <div><p className="font-medium text-gray-900">{label}</p><p className="text-sm text-gray-500">{sub}</p></div>
                  <SimpleToggle checked={formData[field]} onChange={(v) => updateField(field, v)} testId={testId} />
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Contacto</h2>
              <p className="text-gray-600 mt-2">¿Cómo pueden contactarte?</p>
            </div>
            <div><Label htmlFor="contact_email">Email de contacto</Label><Input id="contact_email" type="email" value={formData.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} placeholder="tu@email.com" className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" data-testid="contact-email-input" /></div>
            <div><Label htmlFor="contact_link">Enlace externo (opcional)</Label><Input id="contact_link" value={formData.contact_link} onChange={(e) => updateField('contact_link', e.target.value)} placeholder="Instagram, LinkedIn, etc." className="mt-1 rounded-xl bg-[hsl(144,18%,97%)] border-[hsl(144,18%,85%)]" data-testid="contact-link-input" /></div>
            <div className="p-4 bg-[hsl(144,18%,88%)] rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Permitir contacto directo</p>
                  <p className="text-sm text-gray-600 mt-1">Si activas esta opción, cualquier usuario podrá ver tu información de contacto sin necesidad de interés mutuo.</p>
                </div>
                <SimpleToggle checked={formData.allow_direct_contact} onChange={(v) => updateField('allow_direct_contact', v)} testId="allow-direct-contact-switch" />
              </div>
            </div>
            <div className="p-4 bg-[hsl(144,18%,93%)] rounded-xl">
              <p className="text-sm text-gray-600"><strong>¿Cómo funciona?</strong> Por defecto, tu contacto solo se muestra cuando hay interés mutuo. Si activas "contacto directo", cualquiera puede ver tu email y enlace.</p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Test de convivencia</h2>
              <p className="text-gray-600 mt-2">Elige la opción con la que más te identifiques en cada bloque</p>
            </div>
            {TEST_BLOCKS.map((block) => (
              <div key={block.id} className="space-y-3">
                <p className="font-semibold text-gray-800">{block.emoji} {block.title}</p>
                <div className="space-y-2">
                  {block.options.map((opt) => {
                    const selected = testAnswers[block.id] === opt.value;
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => setTestAnswers(prev => ({ ...prev, [block.id]: opt.value }))}
                        className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                          selected
                            ? 'bg-[hsl(144,18%,40%)] border-[hsl(144,18%,40%)] text-white font-medium shadow-sm'
                            : 'bg-[hsl(144,18%,97%)] border-[hsl(144,18%,80%)] text-gray-700 hover:border-[hsl(144,18%,55%)] hover:bg-[hsl(144,18%,90%)]'
                        }`}>
                        <span className="flex items-center gap-2">
                          {selected && <Check className="w-4 h-4 flex-shrink-0" />}
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {TEST_BLOCKS.every(b => testAnswers[b.id] !== null) && (
              <div className="mt-4 p-4 rounded-xl text-center border-2 border-dashed border-[hsl(144,18%,70%)] bg-[hsl(144,18%,95%)]">
                <p className="text-sm text-gray-500 mb-1">Tu perfil de convivencia</p>
                {(() => {
                  const p = calcRoommateProfile(testAnswers);
                  return <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold border ${PROFILE_STYLES[p]}`}>{p}</span>;
                })()}
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-gray-900">Tus gustos</h2>
              <p className="text-gray-600 mt-2">
                Elige hasta <strong>6 actividades</strong> que te representen
                <span className="ml-1 text-[hsl(144,18%,45%)] font-medium">({selectedActivities.length}/6)</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_ACTIVITIES.map((activity) => {
                const selected = selectedActivities.includes(activity);
                return (
                  <button key={activity} type="button" onClick={() => toggleActivity(activity)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      selected
                        ? 'bg-[hsl(144,18%,45%)] border-[hsl(144,18%,45%)] text-white'
                        : 'bg-[hsl(144,18%,97%)] border-[hsl(144,18%,80%)] text-gray-700 hover:border-[hsl(144,18%,55%)] hover:bg-[hsl(144,18%,90%)]'
                    }`}>
                    {selected && <Check className="w-3 h-3 inline mr-1" />}
                    {activity}
                  </button>
                );
              })}
            </div>
            {selectedActivities.length > 0 && (
              <div className="mt-2 p-3 bg-[hsl(144,18%,93%)] rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Seleccionadas:</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedActivities.map(a => (
                    <span key={a} className="bg-amber-100 text-amber-700 border border-amber-200 text-xs px-2 py-0.5 rounded-full font-medium">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(144,18%,93%)] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex flex-col items-center gap-1 mb-8">
          <img src={logoImg} alt="Partykatu" className="h-12 w-auto" />
          <span className="font-heading font-semibold text-xl text-[hsl(144,18%,38%)] tracking-tight" translate="no">Partykatu</span>
        </div>
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div key={s} className={`flex-1 h-2 rounded-full mx-0.5 transition-colors ${s <= step ? 'bg-[hsl(144,18%,45%)]' : 'bg-[hsl(144,18%,80%)]'}`} />
            ))}
          </div>
          <p className="text-center text-sm text-[hsl(144,18%,35%)]">Paso {step} de {totalSteps}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(144,18%,85%)] p-6 sm:p-8">
          {renderStep()}
          <div className="flex justify-between mt-8 pt-6 border-t border-[hsl(144,18%,88%)]">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={handleBack} className="rounded-xl border-[hsl(144,18%,70%)]" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />Atrás
              </Button>
            ) : <div />}
            {step < totalSteps ? (
              <Button type="button" onClick={handleNext} className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl" data-testid="next-btn">
                Siguiente<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl" disabled={loading} data-testid="complete-btn">
                {loading ? 'Guardando...' : 'Completar perfil'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {cropModalSrc && (
        <ImageCropModal
          imageSrc={cropModalSrc}
          title="Ajustar foto de perfil"
          onCropComplete={handleCropComplete}
          onCancel={() => setCropModalSrc(null)}
        />
      )}
    </div>
  );
}
